import React from "react";
import { Box, Typography, Paper, Chip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createRecipeStyles } from "./styles";
import { TimeIcon } from "../ui/RecipeIcons";
import { FetchedRecipe } from "../../types/recipe";

/**
 * Props for RecipeDescription component.
 * @property {FetchedRecipe} recipe - The recipe object containing details to display.
 */
interface RecipeDescriptionProps {
  recipe: FetchedRecipe;
}

/**
 * RecipeDescription Component
 *
 * Displays the recipe's description, prep time, tools, and process steps.
 * Uses responsive layout and Material UI components for styling.
 *
 * Features:
 * - Shows description and prep time (with icon)
 * - Lists tools and processes as chips
 * - Handles missing data gracefully
 *
 * @param {RecipeDescriptionProps} props - Component props
 * @returns {JSX.Element} The rendered recipe description section
 */
const RecipeDescription: React.FC<RecipeDescriptionProps> = ({ recipe }) => {
  const theme = useTheme();
  const styles = createRecipeStyles(theme);

  return (
    <Paper elevation={0} sx={styles.page.descriptionSection}>
      {/* Main details: description and prep time */}
      <Box sx={styles.page.descriptionDetails}>
        {/* Description text */}
        <Box sx={styles.page.sectionText}>
          <Typography variant="subtitle1" sx={styles.page.detailTitle}>
            Description
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {recipe.data.details.description || "No description provided."}
          </Typography>
        </Box>
        {/* Prep time with icon */}
        <Box sx={styles.page.sectionText}>
          <Typography variant="subtitle1" sx={styles.page.detailTitle}>
            Prep time
          </Typography>
          {recipe.data.details.prepTime ? (
            <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
              <TimeIcon />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                {recipe.data.details.prepTime} Minutes
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              N/A
            </Typography>
          )}
        </Box>
      </Box>
      {/* Tools and Process chips */}
      <Box sx={styles.page.toolsAndProcess}>
        {/* Tools list */}
        <Box sx={styles.page.sectionText}>
          <Typography variant="subtitle1" sx={styles.page.detailTitle}>
            Tools
          </Typography>
          <Box sx={styles.page.chipContainer}>
            {Array.isArray(recipe.data.details.tools) &&
              recipe.data.details.tools.map(
                (tool, index) =>
                  tool &&
                  tool.name && (
                    <Chip key={index} label={tool.name} sx={styles.page.chip} />
                  )
              )}
            {/* Fallback if no tools */}
            {(!recipe.data.details.tools ||
              recipe.data.details.tools.length === 0) && (
              <Typography variant="body2" color="text.secondary">
                No tools listed.
              </Typography>
            )}
          </Box>
        </Box>
        {/* Process list */}
        <Box sx={styles.page.sectionText}>
          <Typography variant="subtitle1" sx={styles.page.detailTitle}>
            Process
          </Typography>
          <Box sx={styles.page.chipContainer}>
            {Array.isArray(recipe.data.details.processes) &&
              recipe.data.details.processes.map(
                (process, index) =>
                  process &&
                  process.name && (
                    <Chip
                      key={index}
                      label={process.name}
                      sx={styles.page.chip}
                    />
                  )
              )}
            {/* Fallback if no processes */}
            {(!recipe.data.details.processes ||
              recipe.data.details.processes.length === 0) && (
              <Typography variant="body2" color="text.secondary">
                No process steps listed.
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default RecipeDescription;
